import { Button, Input, Modal, Radio } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalActionButtons } from 'components/ui';

import {
  CollectionHeader,
  EditableValueContainer,
  EditableValueInline,
  EditableValueText,
  EditorPanel,
  ErrorText,
  InlineEditAction,
  JsonChildRow,
  JsonCollection,
  ModeGroup,
  RawTextArea,
  ValueActionRow,
} from './JsonField.style';
import { type TJsonEditMode, type TJsonPath } from './jsonField.types';
import {
  getAtPath,
  inferJsonFieldType,
  isPlainObject,
  parseEditedJsonValue,
  parseJsonObjectString,
  pathToKey,
  renderPrimitiveJsonValue,
  setAtPath,
} from './jsonField.utils';

type Props = {
  fieldKey: string;
  defaultValue: string;
  onCommit?: (data: any) => void;
  onCancelEdit?: () => void;
};

export const JsonFieldEditInput = ({ fieldKey, defaultValue, onCommit, onCancelEdit }: Props) => {
  const { t } = useTranslation();
  const parsed = parseJsonObjectString(defaultValue) || {};
  const [draftJson, setDraftJson] = useState<unknown>(parsed);
  const [hoveredPathKey, setHoveredPathKey] = useState<string | null>(null);
  const [editingPathKey, setEditingPathKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [errorText, setErrorText] = useState('');
  const [editMode, setEditMode] = useState<TJsonEditMode>('structured');
  const [rawText, setRawText] = useState(defaultValue);

  const handleBeginEdit = (path: TJsonPath) => {
    const previousValue = getAtPath(draftJson, path);
    const type = inferJsonFieldType(previousValue);
    setErrorText('');
    setEditingPathKey(pathToKey(path));
    setEditingValue(
      type === 'string' ? String(previousValue ?? '') : JSON.stringify(previousValue, null, 2)
    );
  };

  const handleSaveEditedPath = () => {
    if (!editingPathKey) {
      return;
    }
    const path = JSON.parse(editingPathKey) as TJsonPath;
    const previousValue = getAtPath(draftJson, path);
    const { value: nextValue, error } = parseEditedJsonValue(editingValue, previousValue);

    if (error) {
      setErrorText(error);
      return;
    }

    setDraftJson((current) => setAtPath(current, path, nextValue));
    setEditingPathKey(null);
    setEditingValue('');
    setErrorText('');
  };

  const renderEditorValue = (node: unknown, path: TJsonPath, depth = 0): JSX.Element => {
    const currentPathKey = pathToKey(path);
    const isHovered = hoveredPathKey === currentPathKey;
    const isEditing = editingPathKey === currentPathKey;

    if (isEditing) {
      return (
        <EditableValueContainer $depth={depth} $active={isHovered || isEditing}>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 8 }}
            value={editingValue}
            onChange={(event) => setEditingValue(event.target.value)}
          />
          <ValueActionRow>
            <Button type="primary" size="small" onClick={handleSaveEditedPath}>
              {t('components.saveValue', 'Save Value')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setEditingPathKey(null);
                setEditingValue('');
                setErrorText('');
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </ValueActionRow>
        </EditableValueContainer>
      );
    }

    if (isPlainObject(node)) {
      const entries = Object.entries(node);
      return (
        <JsonCollection
          $depth={depth}
          $active={isHovered}
          onMouseEnter={() => setHoveredPathKey(currentPathKey)}
          onMouseLeave={() => setHoveredPathKey(null)}
        >
          <CollectionHeader>
            <span>{'{'}</span>
            {path.length > 0 ? (
              <InlineEditAction $visible={isHovered}>
                <Button type="link" size="small" onClick={() => handleBeginEdit(path)}>
                  {t('common.edit', 'Edit')}
                </Button>
              </InlineEditAction>
            ) : null}
          </CollectionHeader>
          {entries.map(([entryKey, entryValue], index) => (
            <JsonChildRow key={`${currentPathKey}-${entryKey}`}>
              <span>{`${entryKey}: `}</span>
              {renderEditorValue(entryValue, [...path, entryKey], depth + 1)}
              {index < entries.length - 1 ? <span>,</span> : null}
            </JsonChildRow>
          ))}
          <div>{'}'}</div>
        </JsonCollection>
      );
    }

    if (Array.isArray(node)) {
      return (
        <JsonCollection
          $depth={depth}
          $active={isHovered}
          onMouseEnter={() => setHoveredPathKey(currentPathKey)}
          onMouseLeave={() => setHoveredPathKey(null)}
        >
          <CollectionHeader>
            <span>{'['}</span>
            {path.length > 0 ? (
              <InlineEditAction $visible={isHovered}>
                <Button type="link" size="small" onClick={() => handleBeginEdit(path)}>
                  {t('common.edit', 'Edit')}
                </Button>
              </InlineEditAction>
            ) : null}
          </CollectionHeader>
          {node.map((item, index) => (
            <JsonChildRow key={`${currentPathKey}-${index}`}>
              {renderEditorValue(item, [...path, index], depth + 1)}
              {index < node.length - 1 ? <span>,</span> : null}
            </JsonChildRow>
          ))}
          <div>]</div>
        </JsonCollection>
      );
    }

    return (
      <EditableValueInline
        $depth={depth}
        $active={isHovered || isEditing}
        onMouseEnter={() => setHoveredPathKey(currentPathKey)}
        onMouseLeave={() => setHoveredPathKey(null)}
      >
        <EditableValueText>{renderPrimitiveJsonValue(node)}</EditableValueText>
        <InlineEditAction $visible={isHovered}>
          <Button type="link" size="small" onClick={() => handleBeginEdit(path)}>
            {t('common.edit', 'Edit')}
          </Button>
        </InlineEditAction>
      </EditableValueInline>
    );
  };

  return (
    <>
      <span>{t('components.json', 'JSON')}</span>
      <Modal
        open={true}
        title={t('components.editJson', 'Edit JSON')}
        footer={null}
        onCancel={() => {
          onCancelEdit?.();
        }}
      >
        <ModeGroup
          value={editMode}
          onChange={(event) => {
            const nextEditMode = event.target.value as TJsonEditMode;

            if (nextEditMode === 'raw') {
              setRawText(JSON.stringify(draftJson, null, 2));
            }

            if (nextEditMode === 'structured' && editMode === 'raw') {
              if (!rawText.trim()) {
                setErrorText(t('components.valueCannotBeEmpty', 'Value cannot be empty.'));
                return;
              }

              try {
                setDraftJson(JSON.parse(rawText));
              } catch {
                setErrorText(
                  t(
                    'components.rawTextInvalidJson',
                    'Raw text must be valid JSON to use structured editing.'
                  )
                );
                return;
              }
            }

            setEditMode(nextEditMode);
            setErrorText('');
          }}
        >
          <Radio.Button value="structured">{t('components.structured', 'Structured')}</Radio.Button>
          <Radio.Button value="raw">{t('components.rawText', 'Raw text')}</Radio.Button>
        </ModeGroup>
        {editMode === 'structured' ? (
          <EditorPanel>{renderEditorValue(draftJson, [])}</EditorPanel>
        ) : (
          <RawTextArea
            autoSize={{ minRows: 8, maxRows: 16 }}
            value={rawText}
            onChange={(event) => setRawText(event.target.value)}
          />
        )}
        {errorText ? <ErrorText>{errorText}</ErrorText> : null}
        <ModalActionButtons>
          <Button
            onClick={() => {
              onCancelEdit?.();
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              if (editMode === 'raw' && !rawText.trim()) {
                setErrorText(t('components.valueCannotBeEmpty', 'Value cannot be empty.'));
                return;
              }

              const nextValue = {
                [fieldKey]: editMode === 'raw' ? rawText : JSON.stringify(draftJson),
              };
              onCommit?.(nextValue);
            }}
          >
            {t('common.apply', 'Apply')}
          </Button>
        </ModalActionButtons>
      </Modal>
    </>
  );
};
